<template>
  <article class="jps-curation-renderer" :class="theme">
    <div v-if="!curation" class="curation-renderer-loading">
      <div :class="{'hina-logo-icon hina-loading': isHina, 'jps-logo-icon jps-loading': isJps}"></div>
    </div>
    <template v-else>
      <Component
        :is="headerComponent"
        :curation="curation"
        :db="db"
        :org="org"
        :page="page"
        :nointeract="nointeract"
        :parent-curation="parentCuration"
      />

      <div>
        <CurationPartsRenderer :curation="curation" :parts="curation.parts" />
      </div>

      <Component
        v-if="!nohooter"
        :is="footerComponent"
        :curation="curation"
        :db="db"
        :org="org"
        :page="page"
        :parent-curation="parentCuration"
      />
      <div class="curation-edit-button-div" v-if="showEdit">
        <button
          class="curation-edit-button is-hidden-mobile"
          @click="emits('edit')"
          aria-hidden="true"
        >
          <BaseIcon name="mdi/pencil" />
        </button>
      </div>
    </template>
  </article>
</template>

<script lang="ts" setup>
const { isJps, isHina } = useServiceType()

const CurationFooterDatabase = defineAsyncComponent(
  () => import('./footer/CurationFooterDatabase.vue'),
)
const CurationFooterGallery = defineAsyncComponent(
  () => import('./footer/CurationFooterGallery.vue'),
)
const CurationFooterOrganization = defineAsyncComponent(
  () => import('./footer/CurationFooterOrganization.vue'),
)
const CurationFooterPage = defineAsyncComponent(
  () => import('./footer/CurationFooterPage.vue'),
)
const CurationFooterSubpage = defineAsyncComponent(
  () => import('./footer/CurationFooterSubpage.vue'),
)
const CurationHeaderDatabase = defineAsyncComponent(
  () => import('./header/CurationHeaderDatabase.vue'),
)
const CurationHeaderGallery = defineAsyncComponent(
  () => import('./header/CurationHeaderGallery.vue'),
)
const CurationHeaderOrganization = defineAsyncComponent(
  () => import('./header/CurationHeaderOrganization.vue'),
)
const CurationHeaderPage = defineAsyncComponent(
  () => import('./header/CurationHeaderPage.vue'),
)
const CurationHeaderSubpage = defineAsyncComponent(
  () => import('./header/CurationHeaderSubpage.vue'),
)

const dataStore = useDataStore()

interface Props {
  curation?: Curation
  noheader?: boolean
  nohooter?: boolean
  parentCuration?: Curation
  showEdit?: boolean
  nointeract?: boolean
  page?: Page
}
const props = withDefaults(defineProps<Props>(), {
  noheader: false,
  nohooter: false,
  parentCuration: null,
  showEdit: false,
  nointeract: false,
  page: null,
})
const emits = defineEmits(['edit'])

const headerComponent = computed(() => {
  if (props.noheader) return null
  if (!props.curation) return null
  switch (props.curation.curationType) {
    case 'DB':
      return CurationHeaderDatabase
    case 'ORGANIZATION':
      return CurationHeaderOrganization
    case 'GALLERY':
      return CurationHeaderGallery
    case 'SUBPAGE':
      return CurationHeaderSubpage
    case 'PAGE':
      return CurationHeaderPage
  }
  return null
})

const footerComponent = computed(() => {
  if (props.noheader) return null
  if (!props.curation) return null
  switch (props.curation.curationType) {
    case 'DB':
      return CurationFooterDatabase
    case 'ORGANIZATION':
      return CurationFooterOrganization
    case 'GALLERY':
      return CurationFooterGallery
    case 'SUBPAGE':
      return CurationFooterSubpage
    case 'PAGE':
      return CurationFooterPage
  }
  return null
})

const isDB = computed(() => {
  if (!props.curation) return false
  return props.curation.curationType === 'DB'
})

const isOrg = computed(() => {
  if (!props.curation) return false
  return props.curation.curationType === 'ORGANIZATION'
})

const isGallery = computed(() => {
  if (!props.curation) return false
  return props.curation.curationType === 'GALLERY'
})

const theme = computed(() => {
  if (isGallery.value && props.curation.theme)
    return 'curation-theme-' + props.curation.theme
})

const isPage = computed(() => {
  if (!props.curation) return false
  return props.curation.curationType === 'PAGE'
})

const db = computed(() => {
  if (isDB.value) {
    return dataStore.database(props.curation.id.split(/-/)[0])
  }
  return null
})

const org = computed(() => {
  if (isOrg.value) {
    return dataStore.organization(props.curation.id.split(/-/)[0])
  }
  return null
})
</script>

<style lang="scss">
@use '@/assets/styles/scss/vue' as *;
@use '@/assets/styles/scss/components/_modern';
@use '@/assets/styles/scss/components/_wa-modern';

.jps-curation-renderer {
  /* overflow: hidden; */
  .curation-edit-button-div {
    text-align: center;
    margin-top: 2rem;
    margin-bottom: 3rem;
    .curation-edit-button {
      .jps-icon {
        margin: 0;
      }
      cursor: pointer;
      @include no-user-select;
      appearance: none;
      -webkit-tap-highlight-color: transparent;

      border: none;
      color: white;
      background-color: $jps-gray-300;
      border-radius: 50%;
      width: 2rem;
      height: 2rem;
      margin: auto;

      transition: all 0.2s;
      &:hover {
        background: $jps-sub-orange-hover;
        border-radius: 50%;
        transition: all 0.2s;
      }
    }
  }

  .curation-renderer-loading {
    min-height: calc(100vh - 3.5rem - 220px);
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
